import { useState } from 'react';
import { BAR_OPTIONS, DEFAULT_PLATES_KG, platesFor } from '../lib/plates';
import { Sheet } from './Sheet';

/** Roughly proportional heights so the stack reads like a real loaded bar. */
const PLATE_HEIGHT: Record<number, number> = {
  25: 46,
  20: 42,
  15: 38,
  10: 33,
  5: 27,
  2.5: 22,
  1.25: 18,
};

export function PlateCalculator({
  targetKg,
  open,
  onClose,
}: {
  targetKg: number;
  open: boolean;
  onClose: () => void;
}) {
  const [barKg, setBarKg] = useState(20);
  const load = platesFor(targetKg, barKg, DEFAULT_PLATES_KG);

  return (
    <Sheet open={open} title={`Loading ${targetKg} kg`} onClose={onClose}>
      <div className="stack">
        <div className="field">
          <label htmlFor="plate-bar">Bar</label>
          <select id="plate-bar" value={barKg} onChange={(e) => setBarKg(Number(e.target.value))}>
            {BAR_OPTIONS.map((bar) => (
              <option key={bar.kg} value={bar.kg}>
                {bar.name} — {bar.kg} kg
              </option>
            ))}
          </select>
        </div>

        {!load.possible ? (
          <div className="warning">
            <span>
              {targetKg} kg is less than the {barKg} kg bar on its own. Pick a lighter bar or use
              dumbbells.
            </span>
          </div>
        ) : (
          <>
            <div className="card" style={{ background: 'var(--surface-2)' }}>
              <div className="card-title">Per side</div>

              {load.perSide.length === 0 ? (
                <div className="muted">Just the empty bar.</div>
              ) : (
                <>
                  <div className="platestack">
                    {load.perSide.flatMap((plate) =>
                      Array.from({ length: plate.count }, (_, i) => (
                        <span
                          key={`${plate.kg}-${i}`}
                          className="plate"
                          style={{ height: PLATE_HEIGHT[plate.kg] ?? 20 }}
                        >
                          {plate.kg}
                        </span>
                      )),
                    )}
                  </div>
                  <div className="row wrap" style={{ gap: 6, marginTop: 12 }}>
                    {load.perSide.map((plate) => (
                      <span className="pill accent" key={plate.kg}>
                        {plate.count} × {plate.kg} kg
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="grid-2">
              <div className="stat">
                <div className="label">Bar loads to</div>
                <div className="value">
                  {load.achievableKg}
                  <span className="unit"> kg</span>
                </div>
              </div>
              <div className="stat">
                <div className="label">Per side</div>
                <div className="value">
                  {(load.achievableKg - load.barKg) / 2}
                  <span className="unit"> kg</span>
                </div>
              </div>
            </div>

            {load.shortfallKg !== 0 && (
              <div className="warning">
                <span>
                  The plates can't make exactly {targetKg} kg — closest is {load.achievableKg} kg,{' '}
                  {Math.abs(load.shortfallKg)} kg
                  {load.shortfallKg > 0 ? ' under' : ' over'}. Use that and log what you lifted.
                </span>
              </div>
            )}
          </>
        )}

        <div className="tiny faint">
          Assumes a standard plate set down to 1.25 kg. If your gym has micro plates or is missing a
          size, the numbers shift slightly.
        </div>
      </div>
    </Sheet>
  );
}
